import "./inputs.scss";
import React from "react";
import RetractableInput from "./RetractableInput";

export type PaddingType = {
    top: null | number | string;
    left: null | number | string;
    right: null | number | string;
    bottom: null | number | string;
};

type Props = {
    name: string;
    padding: PaddingType;
    onChange: (name: string, padding: PaddingType) => void;
};

export function createStyleFromPaddingEdge(value: null | number | string): string | null {
    if(value === null) {
        return null;
    }

    if(typeof value === "string") {
        value = parseFloat(value);
    }

    if(value && !isNaN(value)) {
        return `${value}px`;
    }

    return null;
}

type PaddingStyleType = {
    boxSizing: "border-box";
    paddingTop?: string;
    paddingLeft?: string;
    paddingRight?: string;
    paddingBottom?: string;
};

export function createStyleFromPadding(padding: PaddingType): PaddingStyleType {
    if(!(padding && typeof padding === "object")) {
        return {
            boxSizing: "border-box",
        };
    }

    const top = createStyleFromPaddingEdge(padding.top);
    const left = createStyleFromPaddingEdge(padding.left);
    const right = createStyleFromPaddingEdge(padding.right);
    const bottom = createStyleFromPaddingEdge(padding.bottom);

    const results: PaddingStyleType = {
        boxSizing: "border-box",
    };

    if(top) results.paddingTop = top;

    if(left) results.paddingLeft = left;

    if(right) results.paddingRight = right;

    if(bottom) results.paddingBottom = bottom;

    return results;
}

interface State {
    focus: null | string;
}

export default class PaddingInput extends React.Component<Props, State> {
    public state: State = {
        focus: null,
    };

    public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.props.onChange(this.props.name, {
            ...this.props.padding,
            [name]: value,
        });
    };

    public renderInput = (label: string, name: string, value: any): React.ReactNode => {
        return (
            <RetractableInput hasValue={!!value} label={label}>
                {(onFocus, onBlur): React.ReactNode => {
                    const focus = (): void => {
                        this.setState({
                            focus: name,
                        });

                        onFocus();
                    };

                    const blur = (): void => {
                        this.setState({
                            focus: null,
                        });
                        onBlur();
                    };

                    return (
                        <input
                            type="number"
                            step="any"
                            name={name}
                            min={0}
                            value={value ?? ""}
                            onFocus={focus}
                            onBlur={blur}
                            onChange={this.handleChange}
                        />
                    );
                }}
            </RetractableInput>
        );
    };

    public renderDisplay = (name: string, value: any): React.ReactNode => {
        return (
            <div
                className={"padding-display-item " + name + (this.state.focus === name ? " selected" : "")}
            >
                {((!value || isNaN(value)) ? "-" : value)}
            </div>
        );
    };

    public render(): React.ReactNode {
        const { top, left, right, bottom } = this.props.padding;

        return (
            <div className="padding-input">
                <div className="padding-display">
                    {this.renderDisplay("top", top)}
                    {this.renderDisplay("left", left)}
                    {this.renderDisplay("right", right)}
                    {this.renderDisplay("bottom", bottom)}
                </div>
                <div className="padding-inputs">
                    {this.renderInput("Top", "top", top)}
                    {this.renderInput("Left", "left", left)}
                    {this.renderInput("Right", "right", right)}
                    {this.renderInput("Bottom", "bottom", bottom)}
                </div>
            </div>
        );
    }
}
